import { ContentType } from "../Common/ContentType";
import { ContentTypeToStorageKeyMap } from "../Common/ContentType";
import { EventIds } from "../Logging/Constants";
import * as Logging from "../Logging/Logger";
import * as Caching from "../Utils/Caching";
import * as Storage from "../Utils/Storage";
import * as Utils from "../Utils/Utils";

const { isNOU } = Utils;

export const enum SettingKey {
	// General
	logLevelSettings = "LogLevelSettings",

	// Web surfaces
	teachingMessageCooldown = "TeachingMessageCooldown",
	webSurfacesIntLink = "WebSurfacesIntLink",
	webSurfacesProdLink = "WebSurfacesProdLink",

	// TMS settings
	tmsAppId = "TmsAppId",
	tmsServiceUrl = "TmsServiceUrl",
	tmsActionUrl = "TmsActionUrl",
	tmsLoadTimeout = "TmsLoadTimeout",
	tmsMessageMetadataExpiry = "TmsMessageMetadataExpiry",
	tmsUserGovernanceRulesExpiry = "TmsUserGovernanceRulesExpiry",
	tmsCampaignContentExpiry = "TmsCampaignContentExpiry",
	tmsDynamicSettingsExpiry = "TmsDynamicSettingsExpiry",
	tmsLogLevelSettingsExpiry = "TmsLogLevelSettingsExpiry",
	tmsUserFactsExpiry = "TmsUserFactsExpiry",

	// Tulips
	tulipsAppId = "TulipsAppId",
	tulipsLoadTimeout = "TulipsLoadTimeout",
	tulipsServiceUrl = "TulipsServiceUrl",
	tulipsIngestionTimeInterval = "TulipsIngestionTimeInterval",

	// AugLoop
	tulipsAugLoopAnnotationName = "TulipsAugLoopAnnotationName",
	tulipsAugLoopPackageDelayLoad = "TulipsAugLoopPackageDelayLoad",
	tulipsAugLoopLoadRetryTimes = "TulipsAugLoopLoadRetryTimes",
	tulipsAugLoopLoadTimeout = "TulipsAugLoopLoadTimeout",
}

let dynamicSettingsTmsgCache: { [key: string]: Caching.ICacheItem } = {};

function parseObjectFromCache(tempItem: string): Caching.ICacheItem {
	try {
		const tempCacheItem: Caching.ICacheItem = JSON.parse(tempItem);
		return tempCacheItem;
	} catch (e) {
		return null;
	}
}

export function getDynamicSetting<T>(key: SettingKey, defaultValue: T): T {
	try {
		dynamicSettingsTmsgCache = {};
		if (Storage.isStorageAvailable) {
			const tempStorageItem: string = Storage.readObjectFromStorage(ContentTypeToStorageKeyMap[ContentType.dynamicSettings]);
			if (!isNOU(tempStorageItem)) {
				const tempCacheItem: Caching.ICacheItem = parseObjectFromCache(tempStorageItem);
				if (!isNOU(tempCacheItem)) {
					tempCacheItem.expiryDate = typeof tempCacheItem.expiryDate === "string" ?
						new Date(tempCacheItem.expiryDate) : tempCacheItem.expiryDate;
					dynamicSettingsTmsgCache[tempCacheItem.contentType] = tempCacheItem;
				}
			}
		}

		if (dynamicSettingsTmsgCache.isNOU) {
			return defaultValue;
		}

		const settingsObj = (dynamicSettingsTmsgCache[ContentType.dynamicSettings]
			&& dynamicSettingsTmsgCache[ContentType.dynamicSettings].content) as Record<string, T>;
		if (!isNOU(settingsObj)
			&& Object.prototype.hasOwnProperty.call(settingsObj, key)
			&& !isNOU(settingsObj[key])) {
			const settingValue = settingsObj[key];

			// parse types
			if (typeof defaultValue === "number" && typeof settingValue !== "number") {
				if (typeof settingValue === "string") {
					const returnValue = settingValue.indexOf(".") === -1 ? parseInt(settingValue, 10) : parseFloat(settingValue);
					return isNaN(returnValue) ? defaultValue : returnValue as unknown as T;
				}

				return defaultValue;
			}

			return settingValue;
		}
	} catch (error) {
		Logging.getLogger().error(
			EventIds.CommonError,
			{
				ErrorMessage: `Error reading dynamic setting ${error && error.message}`,
				Type: key,
			});
	}

	return defaultValue;
}

export function resetDynamicSettingsTmsgCache() {
	dynamicSettingsTmsgCache = {};
}
